import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import type { RootState } from '../../store';
import { setFlows, setActiveFlow, addFlow, updateFlow, removeFlow, toggleFlowEnabled } from '../../store/slices/flowSlice';
import { flowService } from '../../services/flow';
import type { FlowDefinition } from '../../types/flow';
import { FlowBuilder } from './FlowBuilder';
import { ToolRegistryView } from './ToolRegistryView';

type ViewTab = 'flows' | 'tools';

const ROLE_ICONS: Record<string, string> = {
  supervisor: '\uD83E\uDDD1\u200D\uD83D\uDCBB',
  medical_analyst: '\uD83C\uDFE5',
  scheduler: '\uD83D\uDCC5',
  gmail_agent: '\u2709\uFE0F',
  log_agent: '\uD83D\uDCDD',
  general_assistant: '\uD83E\uDD16',
};

const ROLE_LABELS: Record<string, string> = {
  supervisor: 'Supervisor',
  medical_analyst: 'Medical Analyst',
  scheduler: 'Scheduler',
  gmail_agent: 'Gmail',
  log_agent: 'Logs',
  general_assistant: 'General',
};

export const FlowsView: React.FC<{
  onSelectFlow?: (flowId: string) => void;
}> = ({ onSelectFlow }) => {
  const dispatch = useDispatch();
  const flows = useSelector((state: RootState) => state.flow?.flows || []);
  const activeFlowId = useSelector((state: RootState) => state.flow?.activeFlowId);
  const [loading, setLoading] = useState(true);
  const [expandedFlow, setExpandedFlow] = useState<string | null>(null);
  const [builderMode, setBuilderMode] = useState<'closed' | 'create' | 'edit'>('closed');
  const [editingFlow, setEditingFlow] = useState<FlowDefinition | null>(null);
  const [activeTab, setActiveTab] = useState<ViewTab>('flows');

  useEffect(() => {
    loadFlows();
  }, []);

  const loadFlows = async () => {
    setLoading(true);
    const loaded = await flowService.listFlows();
    dispatch(setFlows(loaded));
    setLoading(false);
  };

  const handleSelectFlow = (flowId: string) => {
    dispatch(setActiveFlow(flowId));
    onSelectFlow?.(flowId);
  };

  const handleToggleEnabled = async (flow: FlowDefinition) => {
    const updated = await flowService.updateFlow(flow.id, { enabled: !flow.enabled });
    if (updated) {
      dispatch(toggleFlowEnabled({ id: flow.id, enabled: !flow.enabled }));
    }
  };

  const handleDelete = async (flowId: string) => {
    const success = await flowService.deleteFlow(flowId);
    if (success) {
      dispatch(removeFlow(flowId));
      if (activeFlowId === flowId) {
        dispatch(setActiveFlow(null));
      }
    }
  };

  const handleSaveFromBuilder = (saved: FlowDefinition) => {
    const existing = flows.find((f) => f.id === saved.id);
    if (existing) {
      dispatch(updateFlow(saved));
    } else {
      dispatch(addFlow(saved));
    }
    setBuilderMode('closed');
    setEditingFlow(null);
  };

  const handleEditFlow = (flow: FlowDefinition) => {
    setEditingFlow(flow);
    setBuilderMode('edit');
  };

  const handleCreateFlow = () => {
    setEditingFlow(null);
    setBuilderMode('create');
  };

  if (builderMode !== 'closed') {
    return (
      <FlowBuilder
        flow={builderMode === 'edit' ? editingFlow : null}
        onSave={handleSaveFromBuilder}
        onCancel={() => { setBuilderMode('closed'); setEditingFlow(null); }}
      />
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full dark:text-claude-darkTextSecondary text-claude-textSecondary">
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full dark:bg-claude-darkBg bg-claude-bg">
      <div className="px-4 py-3 border-b dark:border-claude-darkBorder border-claude-border">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h2 className="text-lg font-semibold dark:text-claude-darkText text-claude-text">
              Agent Flows & Tools
            </h2>
            <p className="text-xs mt-1 dark:text-claude-darkTextSecondary text-claude-textSecondary">
              Manage flows, tools, and permissions.
            </p>
          </div>
          {activeTab === 'flows' && (
            <button
              type="button"
              onClick={handleCreateFlow}
              className="text-xs px-3 py-1.5 rounded-lg font-medium bg-claude-accent text-white hover:opacity-90 transition-opacity"
            >
              + Create Flow
            </button>
          )}
        </div>
        <div className="flex gap-1">
          <button
            type="button"
            onClick={() => setActiveTab('flows')}
            className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
              activeTab === 'flows'
                ? 'bg-claude-accent text-white'
                : 'dark:bg-claude-darkSurface bg-claude-surface dark:text-claude-darkText text-claude-text hover:opacity-80'
            }`}
          >
            Flows
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('tools')}
            className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
              activeTab === 'tools'
                ? 'bg-claude-accent text-white'
                : 'dark:bg-claude-darkSurface bg-claude-surface dark:text-claude-darkText text-claude-text hover:opacity-80'
            }`}
          >
            Tools
          </button>
        </div>
      </div>

      {activeTab === 'tools' ? (
        <ToolRegistryView />
      ) : (
        <>
          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
            {flows.length === 0 ? (
              <div className="text-center py-8 dark:text-claude-darkTextSecondary text-claude-textSecondary">
                <p className="text-sm">No flows available.</p>
                <p className="text-xs mt-1 opacity-60">Flows are loaded from the server.</p>
              </div>
            ) : (
              flows.map((flow) => (
                <FlowCard
                  key={flow.id}
                  flow={flow}
                  isActive={activeFlowId === flow.id}
                  isExpanded={expandedFlow === flow.id}
                  onSelect={() => handleSelectFlow(flow.id)}
                  onToggleEnabled={() => handleToggleEnabled(flow)}
                  onExpand={() => setExpandedFlow(expandedFlow === flow.id ? null : flow.id)}
                  onDelete={() => handleDelete(flow.id)}
                  onEdit={() => handleEditFlow(flow)}
                />
              ))
            )}
          </div>

          <div className="px-4 py-3 border-t dark:border-claude-darkBorder border-claude-border">
            <p className="text-xs dark:text-claude-darkTextSecondary text-claude-textSecondary">
              {activeFlowId
                ? `Active: ${flows.find(f => f.id === activeFlowId)?.name || activeFlowId}`
                : 'No flow selected — using General Assistant'}
            </p>
          </div>
        </>
      )}
    </div>
  );
};

const FlowCard: React.FC<{
  flow: FlowDefinition;
  isActive: boolean;
  isExpanded: boolean;
  onSelect: () => void;
  onToggleEnabled: () => void;
  onExpand: () => void;
  onDelete: () => void;
  onEdit: () => void;
}> = ({ flow, isActive, isExpanded, onSelect, onToggleEnabled, onExpand, onDelete, onEdit }) => {
  const agentNodes = flow.nodes.filter(n => n.type === 'agent');
  const isMultiAgent = agentNodes.length > 1;

  return (
    <div
      className={`rounded-lg border transition-colors cursor-pointer ${
        isActive
          ? 'border-claude-accent dark:bg-claude-accent/10 bg-claude-accent/5'
          : 'dark:border-claude-darkBorder border-claude-border dark:hover:bg-claude-darkSurfaceHover hover:bg-claude-surfaceHover'
      }`}
    >
      <div className="px-4 py-3" onClick={onSelect}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-base">{isMultiAgent ? '\uD83E\uDDD1\u200D\uD83D\uDCBB' : '\uD83E\uDD16'}</span>
            <div>
              <h3 className="text-sm font-medium dark:text-claude-darkText text-claude-text">
                {flow.name}
              </h3>
              <p className="text-xs dark:text-claude-darkTextSecondary text-claude-textSecondary">
                {flow.description}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onToggleEnabled(); }}
              className={`px-2 py-0.5 rounded text-xs font-medium ${
                flow.enabled
                  ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400'
              }`}
            >
              {flow.enabled ? 'Enabled' : 'Disabled'}
            </button>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onExpand(); }}
              className="text-xs dark:text-claude-darkTextSecondary text-claude-textSecondary hover:text-claude-text"
            >
              {isExpanded ? '\u25B2' : '\u25BC'}
            </button>
          </div>
        </div>

        {isActive && (
          <div className="mt-2 flex items-center gap-1">
            <span className="px-2 py-0.5 rounded-full text-xs bg-claude-accent/10 text-claude-accent font-medium">
              Active
            </span>
          </div>
        )}
      </div>

      {isExpanded && (
        <div className="px-4 pb-3 border-t dark:border-claude-darkBorder/50 border-claude-border/50 pt-3">
          <div className="space-y-2">
            <p className="text-xs font-medium dark:text-claude-darkTextSecondary text-claude-textSecondary">
              Agents ({agentNodes.length}):
            </p>
            {agentNodes.map((node) => (
              <div key={node.id} className="flex items-center gap-2 ml-2">
                <span className="text-sm">{ROLE_ICONS[node.role || ''] || '\uD83E\uDD16'}</span>
                <span className="text-xs dark:text-claude-darkText text-claude-text">
                  {node.label || node.id}
                </span>
                <span className="text-xs dark:text-claude-darkTextSecondary text-claude-textSecondary">
                  ({ROLE_LABELS[node.role || ''] || node.role})
                </span>
              </div>
            ))}
          </div>

          {flow.edges.length > 0 && (
            <div className="mt-2 space-y-1">
              <p className="text-xs font-medium dark:text-claude-darkTextSecondary text-claude-textSecondary">
                Edges ({flow.edges.length}):
              </p>
              {flow.edges.map((edge, idx) => (
                <div key={idx} className="text-xs ml-2 dark:text-claude-darkTextSecondary text-claude-textSecondary">
                  {edge.from} {'\u2192'} {edge.to}
                  {edge.condition && <span className="ml-1 opacity-60">[{edge.condition}]</span>}
                </div>
              ))}
            </div>
          )}

          <div className="mt-3 flex justify-between items-center">
            <span className="text-xs opacity-60 dark:text-claude-darkTextSecondary text-claude-textSecondary">
              v{flow.version}
            </span>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onEdit(); }}
                className="text-xs text-claude-accent hover:underline"
              >
                Edit
              </button>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onDelete(); }}
                className="text-xs text-red-500 hover:text-red-600 dark:text-red-400"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};